/**
 * Go7 Rider — in-app notification sounds.
 *
 * Plays the Go7 tone set from /public/sounds. Three things this handles that a
 * bare `new Audio().play()` does not:
 *
 *  1. AUTOPLAY POLICY — browsers refuse audio until the user has interacted with
 *     the page at least once. `installUnlock()` primes playback on the first
 *     real gesture; until then `play()` fails silently (as it always has in this
 *     app, which is why nothing was ever audible).
 *
 *  2. JOB ALERTS THAT KEEP RINGING — a new job must not be missed, so `new-job`
 *     loops until it is acknowledged. It is capped (`alertMaxMs`) so it cannot
 *     ring forever on an unattended phone, it stops when the app is
 *     backgrounded (the OS push takes over there), and only ONE alert ever plays
 *     at a time so three simultaneous jobs do not layer into noise.
 *
 *  3. THE RIDER'S OWN MUTE SETTING — honours `settings.soundEnabled` from the
 *     rider profile (Settings → Sound), cached in localStorage so the very first
 *     notification after launch respects it without waiting on Firestore.
 */

// Type-only import: erased at compile time, so this creates no runtime cycle
// with notification.service.ts (which imports this module).
import type { NotificationType } from './notification.service';

export type SoundName =
  | 'notification'
  | 'notify'
  | 'success'
  | 'error'
  | 'order-assigned'
  | 'status-update'
  | 'order-complete'
  | 'location-ping'
  | 'chat'
  | 'new-job'
  | 'job-cancelled'
  | 'earnings-credited';

/**
 * Resolve a tone against Vite's BASE_URL.
 *
 * This app is built with `base: '/rider/'`, so a bare `/sounds/x.mp3` would
 * resolve to the domain root and 404. Using BASE_URL makes the tones follow the
 * exact same convention as the app's own JS/CSS, so they work both under the
 * `/rider/` web deployment and in any build that uses a different base.
 */
const soundUrl = (name: SoundName) =>
  `${import.meta.env.BASE_URL}sounds/${name}.mp3`.replace(/([^:]\/)\/+/g, '$1');

/** Notification type → the tone that represents it. */
const TYPE_SOUND: Record<NotificationType, SoundName> = {
  'new-job': 'new-job',
  'job-cancelled': 'job-cancelled',
  'earnings-update': 'earnings-credited',
  message: 'chat',
  system: 'notify',
  'shift-reminder': 'notify',
  promotion: 'notify',
};

/** Tones that must keep repeating until the rider acknowledges them. */
const LOOPING: SoundName[] = ['new-job'];

/** Mix levels — job-critical alerts sit above ambient chatter. */
const VOLUME: Partial<Record<SoundName, number>> = {
  'new-job': 0.85,
  'job-cancelled': 0.8,
  'earnings-credited': 0.6,
  chat: 0.5,
  'location-ping': 0.35,
};
const DEFAULT_VOLUME = 0.6;

/** Hard stop for a looping alert so it cannot ring indefinitely. */
const DEFAULT_ALERT_MAX_MS = 20_000;

const STORAGE_KEY = 'go7_rider_sound_enabled';

class SoundService {
  private cache = new Map<SoundName, HTMLAudioElement>();
  private enabled = true;
  private unlocked = false;
  private unlockInstalled = false;

  private alert: HTMLAudioElement | null = null;
  private alertName: SoundName | null = null;
  private alertTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored !== null) this.enabled = stored !== 'false';
    } catch {
      /* localStorage unavailable — default to enabled */
    }

    // A backgrounded tab must not keep ringing; the OS push notification is the
    // correct channel once the app is hidden.
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stopAlert();
      });
    }
  }

  // ---------------------------------------------------------------- settings

  isEnabled(): boolean {
    return this.enabled;
  }

  /** Set the mute preference (called from Settings). Persists for next launch. */
  setEnabled(value: boolean): void {
    this.enabled = Boolean(value);
    try {
      localStorage.setItem(STORAGE_KEY, String(this.enabled));
    } catch {
      /* ignore */
    }
    if (!this.enabled) this.stopAll();
  }

  /**
   * Adopt the rider's saved profile settings. Used on boot and whenever the
   * rider profile loads, so the Firestore setting wins over the local cache.
   */
  syncFromProfile(settings?: { soundEnabled?: boolean } | null): void {
    if (!settings || settings.soundEnabled === undefined) return;
    this.setEnabled(settings.soundEnabled);
  }

  // ------------------------------------------------------------------ unlock

  /**
   * Prime audio on the first genuine user gesture. Call once at startup.
   *
   * Browsers only grant audio permission to a document that has "user
   * activation", and they require the play() attempt to happen *inside* a
   * gesture handler — so we do it here rather than lazily at notification time.
   */
  installUnlock(): void {
    if (this.unlockInstalled || typeof window === 'undefined') return;
    this.unlockInstalled = true;

    const prime = () => {
      if (this.unlocked) return;
      this.unlocked = true;
      // A silent, zero-volume play/pause is enough to satisfy the policy.
      const el = this.element('notification');
      const previousVolume = el.volume;
      el.volume = 0;
      const done = () => {
        el.pause();
        el.currentTime = 0;
        el.volume = previousVolume;
        el.removeEventListener('ended', done);
      };
      el.addEventListener('ended', done);
      const attempt = el.play();
      if (attempt && typeof attempt.then === 'function') {
        attempt.then(done).catch(done);
      } else {
        done();
      }
    };

    const events: Array<keyof WindowEventMap> = ['pointerdown', 'touchstart', 'keydown'];
    const once = () => {
      prime();
      events.forEach((e) => window.removeEventListener(e, once));
    };
    events.forEach((e) => window.addEventListener(e, once, { passive: true }));
  }

  // ------------------------------------------------------------------ players

  private element(name: SoundName): HTMLAudioElement {
    let el = this.cache.get(name);
    if (!el) {
      el = new Audio(soundUrl(name));
      el.preload = 'auto';
      el.volume = VOLUME[name] ?? DEFAULT_VOLUME;
      this.cache.set(name, el);
    }
    return el;
  }

  /** Preload a tone so the first real alert has no delay. */
  warm(name: SoundName): void {
    try {
      this.element(name).load();
    } catch {
      /* ignore */
    }
  }

  /** Play a one-shot tone. Safe to call when muted or not yet unlocked. */
  play(name: SoundName, options: { restart?: boolean } = {}): void {
    if (!this.enabled) return;
    const el = this.element(name);
    if (options.restart !== false) {
      try {
        el.currentTime = 0;
      } catch {
        /* ignore */
      }
    }
    const attempt = el.play();
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(() => {
        /* autoplay blocked or file missing — never surface to the rider */
      });
    }
  }

  // ------------------------------------------------------------------- alerts

  /**
   * Start a repeating alert. Only one alert plays at a time — a second job
   * replaces the first rather than layering on top of it.
   */
  startAlert(name: SoundName, options: { maxMs?: number } = {}): void {
    if (!this.enabled) return;
    this.stopAlert();

    const el = this.element(name);
    el.loop = true;
    try {
      el.currentTime = 0;
    } catch {
      /* ignore */
    }

    this.alert = el;
    this.alertName = name;

    const maxMs = options.maxMs ?? DEFAULT_ALERT_MAX_MS;
    this.alertTimer = setTimeout(() => this.stopAlert(), maxMs);

    const attempt = el.play();
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(() => {
        /* blocked — stop pretending it is ringing */
        this.stopAlert();
      });
    }
  }

  /** Stop the current repeating alert (acknowledged, accepted, declared, hidden). */
  stopAlert(): void {
    if (this.alertTimer !== null) {
      clearTimeout(this.alertTimer);
      this.alertTimer = null;
    }
    if (this.alert) {
      try {
        this.alert.pause();
        this.alert.currentTime = 0;
      } catch {
        /* ignore */
      }
      this.alert.loop = false;
      this.alert = null;
      this.alertName = null;
    }
  }

  isAlerting(): boolean {
    return this.alert !== null;
  }

  /** Which alert is currently ringing (for UI that shows "acknowledge"). */
  currentAlert(): SoundName | null {
    return this.alertName;
  }

  /** Silence everything — used when muting or logging out. */
  stopAll(): void {
    this.stopAlert();
    this.cache.forEach((el) => {
      try {
        el.pause();
        el.currentTime = 0;
      } catch {
        /* ignore */
      }
    });
  }

  // ------------------------------------------------------------------ routing

  /** Route an incoming notification to the right tone. */
  onNotification(type: string): void {
    const name = TYPE_SOUND[type as NotificationType];
    if (!name) {
      this.play('notify');
      return;
    }
    if (LOOPING.includes(name)) {
      this.startAlert(name);
    } else {
      this.play(name);
    }
  }
}

export const soundService = new SoundService();
